import {
  FacebookIcon,
  LinkedinFilledIcon,
  TwitterIcon,
  WhatsAppIcon
} from 'assets/icons/Social'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { useLoginState } from 'state'
import { downloadFileByBlob } from 'utils'

import { DownloadIcon } from '@heroicons/react/solid'

import { useCertificate } from './certificate.services'
import { BlobDataURLType } from './certificate.types'
import {
  FacebookShareLink,
  getCertificate,
  LinkedinAddCertificateToProfile,
  TwitterShareLink,
  WhatsappShareLink
} from './utils'

const Certificate = () => {
  const { query } = useRouter()
  const { user: loggedInUser } = useLoginState()
  const [generatedCertificate, setGeneratedCertificate] =
    useState<BlobDataURLType>()
  const { cuid: certificateID } = query

  const {
    data: certificateData,
    isLoading,
    error
  } = useCertificate(typeof certificateID === 'string' ? certificateID : '')

  const [errorMessage, setErrorMessage] = useState('')

  const isCurrentUser =
    certificateData?.user_details?.username === loggedInUser?.username &&
    loggedInUser

  useEffect(() => {
    const generateCertificate = async () => {
      if (certificateData) {
        try {
          const { user_details, cuid, certificate_type } = certificateData
          const ctr = await getCertificate(
            user_details.name,
            cuid,
            certificate_type
          )
          setGeneratedCertificate(ctr)
        } catch (e) {
          // @ts-ignore
          setErrorMessage(e?.message)
        }
      }
    }

    generateCertificate()
  }, [certificateData])

  if (errorMessage || error || !certificateID) {
    return (
      <p className="text-red-400 text-center mt-5">
        {errorMessage || 'Certificate with given id not found!'}
      </p>
    )
  }

  if (isLoading || !certificateData || !generatedCertificate) {
    return (
      <div className="flex-1 flex items-center justify-center space-x-3">
        <p className="animate-pulse h-5 w-5 bg-primary-light rounded-full"></p>
        <span className="text-primary-light">Loading...</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col-reverse lg:flex-row items-start my-10 mx-5 lg:space-x-5">
      <div className="space-y-10">
        <div className="flex flex-col space-y-3">
          <div className="text-primary-light text-xl sm:text-4xl">
            {certificateData.title}
          </div>
          <div className="text-zinc-50 max-w-3xl">
            {certificateData.description}
          </div>
        </div>
        {isCurrentUser && (
          <>
            <div className="flex flex-col space-y-3">
              <div className="text-primary-light text-xl sm:text-3xl">
                Share the certificate
              </div>
              <div className="flex space-x-3">
                <a
                  href={FacebookShareLink(certificateData.link)}
                  target="_blank"
                  rel="noopener noreferrer">
                  <FacebookIcon className="h-6 w-6" />
                </a>
                <a
                  href={TwitterShareLink(
                    certificateData.link,
                    'Yo! My certificate.',
                    '#devsnest #Fullstack'
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  <TwitterIcon className="h-6 w-6" />
                </a>

                <a
                  href={WhatsappShareLink(
                    certificateData.link,
                    'Yo! My certificate.'
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  <WhatsAppIcon className="h-6 w-6" />
                </a>
              </div>
            </div>
            <div className="flex flex-col space-y-5 sm:w-min">
              <a
                className="flex justify-center px-5 py-3 space-x-3 items-center bg-blue-600 rounded-lg focus:ring-2 ring-blue-600 ring-offset-2 ring-offset-zinc-900 focus:outline-none"
                href={LinkedinAddCertificateToProfile(certificateData)}
                target="_blank"
                rel="noopener noreferrer">
                <LinkedinFilledIcon className="h-5 w-5" />
                <span className="align-text-bottom whitespace-nowrap">
                  Add to profile
                </span>
              </a>
              <button
                className="flex justify-center px-5 py-3 space-x-3 items-center bg-primary-dark rounded-lg focus:ring-2 ring-primary-dark ring-offset-2 ring-offset-zinc-900 focus:outline-none"
                onClick={() =>
                  generatedCertificate.blob &&
                  downloadFileByBlob(
                    generatedCertificate.blob,
                    'Devsnest Certificate'
                  )
                }>
                <DownloadIcon className="h-5 w-5" />
                <span className="align-text-bottom whitespace-nowrap">
                  Download
                </span>
              </button>
            </div>
          </>
        )}
      </div>
      <div className="flex mb-5 lg:mb-0">
        <div className="w-full rounded-xl">
          <img
            src={generatedCertificate.dataURL}
            alt="certificate"
            className="rounded"
          />
        </div>
      </div>
    </div>
  )
}

Certificate.seo = {
  title: 'Certificate | Devsnest'
}

export default Certificate
